import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Stack, Grid, CircularProgress, Alert, Chip, Divider,
} from '@mui/material';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import StarOutlineOutlinedIcon from '@mui/icons-material/StarOutlineOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import ThumbsUpDownOutlinedIcon from '@mui/icons-material/ThumbsUpDownOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';

import { getOverviewReport } from '../../services/api';
import KpiCard from '../../components/reports/KpiCard';
import FunnelChart from '../../components/reports/FunnelChart';
import ReportFilterBar from '../../components/reports/ReportFilterBar';
import {
  ResponsiveContainer, LineChart, Line, Tooltip, XAxis, Legend,
} from 'recharts';

const fmtPct = (v, d = 1) => v == null ? '—' : `${(v * 100).toFixed(d)}%`;
const fmtInt = (v) => v == null ? '—' : Number(v).toLocaleString();
const fmtUsd = (v) => v == null ? '—' : `$${Number(v).toFixed(2)}`;
const fmtDays = (v) => v == null ? '—' : `${Number(v).toFixed(1)}d`;

const TREND_COLORS = ['#3b82f6', '#16a34a', '#f59e0b', '#dc2626', '#8b5cf6', '#0891b2'];

export default function OverviewReportPage() {
  const [filters, setFilters] = useState({ window: '30d' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await getOverviewReport(filters);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.title || e?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const k = data?.data?.kpis || {};
  const leadFunnel = data?.data?.leadFunnel || [];
  const outreachFunnel = data?.data?.outreachFunnel || [];
  const topSegments = data?.data?.topSegments || [];
  const trend = data?.data?.channelTrend || [];

  // Build trend categories for line chart.
  const trendCats = Array.from(new Set(trend.flatMap((d) => Object.keys(d.categories || {}))));
  const trendChart = trend.map((d) => ({
    date: new Date(d.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    ...trendCats.reduce((acc, c) => ({ ...acc, [c]: d.categories?.[c] || 0 }), {}),
  }));

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <DashboardOutlinedIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Executive Overview</Typography>
        <Box flex={1} />
        <ReportFilterBar value={filters} onChange={setFilters} />
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {/* Pipeline KPIs */}
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Pipeline</Typography>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3} lg={2}><KpiCard label="Total leads" value={fmtInt(k.totalLeads)} icon={GroupOutlinedIcon} /></Grid>
            <Grid item xs={6} md={3} lg={2}><KpiCard label="Qualified (window)" value={fmtInt(k.qualifiedInWindow)} icon={VerifiedOutlinedIcon} tone="info" /></Grid>
            <Grid item xs={6} md={3} lg={2}><KpiCard label="Converted (window)" value={fmtInt(k.convertedInWindow)} icon={HandshakeOutlinedIcon} tone="positive" /></Grid>
            <Grid item xs={6} md={3} lg={2}><KpiCard label="Conversion rate" value={fmtPct(k.conversionRate)} icon={TrendingUpOutlinedIcon} tone="positive" /></Grid>
            <Grid item xs={6} md={3} lg={2}><KpiCard label="Avg time-to-qualify" value={fmtDays(k.avgTimeToQualifyDays)} icon={TimerOutlinedIcon} /></Grid>
            <Grid item xs={6} md={3} lg={2}><KpiCard label="Overdue follow-ups" value={fmtInt(k.overdueFollowUps)} icon={ScheduleOutlinedIcon} tone={k.overdueFollowUps > 0 ? 'negative' : 'positive'} /></Grid>
          </Grid>

          {/* Outreach KPIs */}
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Outreach health</Typography>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}><KpiCard label="Reply rate" value={fmtPct(k.replyRate)} icon={ReplyOutlinedIcon} tone="info" /></Grid>
            <Grid item xs={6} md={3}><KpiCard label="Positive outcomes" value={fmtPct(k.positiveOutcomeRate)} icon={ThumbUpOutlinedIcon} tone="positive" /></Grid>
            <Grid item xs={6} md={3}><KpiCard label="Bounce rate" value={fmtPct(k.bounceRate)} icon={ErrorOutlineOutlinedIcon} tone={k.bounceRate > 0.05 ? 'negative' : 'neutral'} /></Grid>
            <Grid item xs={6} md={3}><KpiCard label="Avg AzotteFitScore" value={k.avgAzotteFit == null ? '—' : k.avgAzotteFit} icon={StarOutlineOutlinedIcon} tone="info" /></Grid>
          </Grid>

          {/* AI quality */}
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>AI quality</Typography>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}><KpiCard label="Verdict freshness" value={fmtPct(k.verdictFreshnessRatio)} icon={AutorenewOutlinedIcon} tone="positive" /></Grid>
            <Grid item xs={6} md={3}><KpiCard label="AI useful rate" value={fmtPct(k.aiUsefulnessRate)} icon={ThumbsUpDownOutlinedIcon} tone="positive" /></Grid>
            <Grid item xs={6} md={3}><KpiCard label="Cost / qualified" value={fmtUsd(k.costPerQualifiedUsd)} icon={AttachMoneyOutlinedIcon} tone="info" /></Grid>
            <Grid item xs={6} md={3}><KpiCard label="Sessions (site)" value={fmtInt(k.sessions)} icon={VisibilityOutlinedIcon} /></Grid>
          </Grid>

          {/* Marketing */}
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Marketing</Typography>
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}><KpiCard label="Identified rate" value={fmtPct(k.identifiedRate)} icon={PersonSearchOutlinedIcon} tone="info" /></Grid>
            <Grid item xs={6} md={9}>
              <KpiCard
                label="Top UTM campaign"
                value={k.topUtmCampaign?.name || '—'}
                sub={k.topUtmCampaign ? `${fmtInt(k.topUtmCampaign.sessions)} sessions · ${fmtInt(k.topUtmCampaign.leads)} leads · ${fmtPct(k.topUtmCampaign.convRate)} conv` : null}
                icon={CampaignOutlinedIcon}
                tone="info"
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          {/* Mini charts */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, height: '100%', cursor: 'pointer' }} onClick={() => navigate('/reports/leads')}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Lead Funnel (current)</Typography>
                <FunnelChart stages={leadFunnel.map((s) => ({ label: s.status, count: s.count }))} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, height: '100%', cursor: 'pointer' }} onClick={() => navigate('/reports/outreach')}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Outreach Funnel ({filters.window})</Typography>
                <FunnelChart stages={outreachFunnel.map((s) => ({ label: s.step, count: s.count }))} color="#16a34a" />
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, height: '100%', cursor: 'pointer' }} onClick={() => navigate('/reports/segmentations')}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Top 5 Segments</Typography>
                <Stack spacing={1}>
                  {topSegments.map((s) => (
                    <Stack key={s.id} direction="row" justifyContent="space-between">
                      <Typography variant="body2">{s.name}</Typography>
                      <Chip size="small" label={fmtInt(s.members)} />
                    </Stack>
                  ))}
                  {topSegments.length === 0 && (
                    <Typography variant="caption" color="text.secondary">No segments yet.</Typography>
                  )}
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 2, cursor: 'pointer' }} onClick={() => navigate('/reports/marketing')}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Channel Mix Trend (last 14 days)</Typography>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={trendChart}>
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    {trendCats.map((c, i) => (
                      <Line key={c} type="monotone" dataKey={c} stroke={TREND_COLORS[i % TREND_COLORS.length]} strokeWidth={2} dot={false} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
